import {
  useWSModalCustom,
  WSActions,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ClientWorkFlowStatus,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import React, { useState } from "react";
import { addNotification } from "../../../components/Notification/Notification";
import { QUERY_PAYABLE, QUERY_PAYABLES } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { bulkActionFactory } from "../../../modules/BulkActions";
import { PayablesTableItemData } from "./PayablesTable";

type Props = {
  data: PayablesTableItemData[];
  onSuccess?: () => void;
} & WSModalCustomProps<any>;

export const ModalBulkApprove: React.FC<Props> = props => {
  const [isLoading, setIsLoading] = useState(false);

  let paidItems: string[] = [];

  const bulkApprove = bulkActionFactory({
    onStart: () => {
      setIsLoading(true);
      paidItems = [];
    },
    action: async (item: PayablesTableItemData) => {
      const payableId = item.payable?.payableId || "";

      if (item.payable?.status === InvoiceStatus.Paid) {
        paidItems.push(payableId);
      } else {
        return await paymentsService.payable.update(payableId, {
          client: {
            workflowStatus: ClientWorkFlowStatus.Approved
          }
        });
      }
    },
    onFinish: ({ successItems, failedItems }) => {
      WSQueryCache.invalidateQueries(QUERY_PAYABLES);
      WSQueryCache.invalidateQueries("QUERY_PAYABLES_TO_APPROVE");
      successItems.forEach(item => {
        WSQueryCache.invalidateQueries([
          QUERY_PAYABLE,
          item.payable?.payableId
        ]);
      });

      addNotification({
        timeout: 6000,
        text:
          `${successItems.length} payable${
            successItems.length > 1 ? "s" : ""
          } have been approved` +
          (paidItems.length > 0
            ? `, ${paidItems.length} ${
                paidItems.length > 1 ? "were" : "was"
              } already paid`
            : "") +
          (failedItems.length > 0 ? `, ${failedItems.length} failed` : "")
      });

      setIsLoading(false);

      props.onSuccess?.();
      props.onClose?.(undefined);
    }
  });

  const payablePluralized = "payable" + (props.data.length > 1 ? "s" : "");

  return (
    <WSModal
      blockClose={isLoading}
      title={`Approve ${props.data.length} ${payablePluralized}?`}
      size="XS"
      onClose={props.onClose}
    >
      <WSText mb="L" color="gray600">
        Approve {payablePluralized} to be paid
      </WSText>

      <WSActions
        buttons={[
          {
            onClick: () => {
              bulkApprove.run(props.data);
            },
            label: "Approve",
            loading: isLoading
          },
          {
            onClick: () => {
              props.onClose?.(undefined);
            },
            label: "Cancel",
            kind: "Tertiary"
          }
        ]}
      />
    </WSModal>
  );
};

export const useModalBulkApprove = () => useWSModalCustom(ModalBulkApprove);
